import * as yup from 'yup';

import { clearPhoneNumberFormatting, formatPhoneNumber } from '@/utils/format-phone-number';

export const removeUselessSpaces = (value: string): string => value?.trim().replace(/  +/g, ' ');

export const validationRegExps = {
  address: /^[A-Za-z0-9\-\s,.#'´]+$/,
  alphabetic: /^[a-zA-Z '.-]+$/,
  alphabeticNoSymbols: /^[a-zA-Z\s]+$/,
  notNumeric: /^\D+$/,
  phoneNumber: /^[0-9-]{12}$/,
  state: /^[A-Z]{2}$/,
  zipCode: /^[0-9]{5}$/,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d\S+]{8,}$/,
  url: /^(http(s):\/\/.)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/,
  numericValue: /^[+-]?(\d*\.)?\d+$/,
  email:
    // eslint-disable-next-line
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
};

export enum ErrorMessages {
  name = 'Please enter a name.',
  companyName = 'Please enter a company name.',
  city = 'Please enter a city.',
  address = 'Please enter an address.',
  phoneNumber = 'Please enter a valid phone number.',
  state = 'Please enter a valid state code.',
  zipCode = 'Please enter a valid ZIP code.',
  email = 'Please enter a valid email address.',
  shingleColor = 'Please select a shingle color',
  form = 'Please fill in the form correctly.',
  password = 'Please enter a valid password.',
  passwordHint = 'The minimum password length is 8 characters and must contain at least 1 lowercase letter, 1 capital letter and 1 number.',
}
export const fieldsValidation = {
  anyString: yup
    .mixed()
    .transform(value => (value ? removeUselessSpaces(value) : ''))
    .nullable(),
  name: yup
    .string()
    .transform(removeUselessSpaces)
    .matches(validationRegExps.alphabetic, ErrorMessages.name)
    .min(2, ErrorMessages.name)
    .max(255, ErrorMessages.name),
  companyName: yup.string().transform(removeUselessSpaces).min(2, ErrorMessages.companyName),
  address: yup
    .string()
    .transform(removeUselessSpaces)
    .matches(validationRegExps.address, ErrorMessages.address)
    .min(4, ErrorMessages.address)
    .max(255, ErrorMessages.address),
  city: yup
    .string()
    .transform(removeUselessSpaces)
    .matches(validationRegExps.alphabeticNoSymbols, ErrorMessages.city)
    .min(2, ErrorMessages.city)
    .max(40, ErrorMessages.city),
  phoneNumber: yup
    .string()
    .transform(value => formatPhoneNumber(clearPhoneNumberFormatting(value)))
    .matches(validationRegExps.phoneNumber, ErrorMessages.phoneNumber),
  state: yup
    .string()
    .transform(value => value?.toUpperCase())
    .matches(validationRegExps.state, ErrorMessages.state),
  zipCode: yup.string().matches(validationRegExps.zipCode, ErrorMessages.zipCode),
  email: yup
    .string()
    .email(ErrorMessages.email)
    .transform(removeUselessSpaces)
    .transform(value => value?.toLowerCase())
    .min(2, ErrorMessages.email)
    .max(255, ErrorMessages.email)
    .matches(validationRegExps.email, ErrorMessages.email),
  password: yup.string(),
};

type FieldsValidation = typeof fieldsValidation;

export const fieldsValidationWithRequired = Object.keys(fieldsValidation).reduce(
  (result, field) => {
    return { ...result, [field]: fieldsValidation[field].required(ErrorMessages[field]) };
  },
  {},
) as FieldsValidation;
